import cloneDeep from "lodash/cloneDeep";

import {
    baseActions,
    BaseActionsProps,
    baseMutations,
    baseState,
    BaseState,
    fetchItems,
    FilterParams,
} from "@/store/modules/helpers/baseState";
import { subsetGetter, SubsetGetterProps, Subsets, subsetSetter } from "@/store/modules/helpers/baseSubset";
import { resetDelayedResetTimeout } from "@/store/modules/helpers/delayedReset";
import { ProfileDetail } from "@/types";
import { convertProfileData } from "@/components/forms/reusables/profileHelper";

export type ProfileDisplay = {
    value: ProfileDetail["id"];
    label: string;
};

type State = BaseState & {
    profiles: Map<ProfileDetail["id"], ProfileDetail>;
};

/**
 * Varje default state ska innehålla BaseState + det som är specifikt för den här modulen.
 * Se helpers/Basestate
 */
const getDefaultState: () => State = () => {
    return {
        profiles: new Map(),
        ...cloneDeep(baseState),
    };
};

const moduleName = "profile";
const collectionName = "profiles";

export default {
    namespaced: true,
    state: getDefaultState(),
    getters: {
        profiles: (state: State) => [...state.profiles.values()],
        profileSubset:
            (state: State) =>
            (
                props: SubsetGetterProps<State> & {
                    // TODO: Kommer den här användas? Ska vi även skicka med showRoll och showOrgUnit isf?
                    displayMode?: boolean;
                }
            ) => {
                const result = subsetGetter<State>({
                    ...props,
                    state,
                    limit: 0,
                    collectionName,
                });

                if (props.displayMode && Array.isArray(result)) {
                    return result.map((profile) => convertProfileData({ profile, showRoll: true, showOrgUnit: true }));
                }

                return result;
            },
        profileById:
            (state: State) =>
            ({ id }: { id?: string }) => {
                if (!id) {
                    return null;
                }

                return state.profiles.get(id);
            },
    },
    /**
     * Mutations ska alltid innehålla baseMutations + det som är specifikt för modulen
     */
    mutations: {
        ...baseMutations({
            reset: (state: State) => {
                Object.assign(state, getDefaultState());

                resetDelayedResetTimeout({ moduleName });
            },
        }),
        addProfiles: (
            state: State,
            {
                offset,
                resetSubset,
                subsetName = "default",
                profiles = [],
            }: {
                resetSubset?: boolean;
                offset: number;
                subsetName: keyof Subsets;
                profiles: ProfileDetail[];
            }
        ) =>
            subsetSetter<State, ProfileDetail>({
                offset,
                subsetName,
                state,
                resetSubset,
                collectionName,
                items: profiles,
            }),
    },
    actions: {
        ...baseActions({
            moduleName,
        }),
        fetchProfiles: async (
            { commit, state }: BaseActionsProps<State>,
            {
                displayMode,
                filterParams = {},
                config = {},
            }: {
                offset?: number;
                filterParams?: FilterParams;
                config?: {
                    tabbnamn?: string;
                };
                displayMode?: boolean;
            }
        ) => {
            try {
                const {
                    subsetName,
                    results,
                    total,
                    nextPage,
                    offset: resultOffset,
                } = await fetchItems<State, ProfileDetail>({
                    filterParams,
                    state,
                    config,
                    page: 1,
                    limit: 0,
                    collectionName,
                    apiUrl: "core/profiles/",
                });

                if (!results) {
                    return {
                        subsetName,
                        total,
                        nextPage,
                    };
                }

                commit("addProfiles", {
                    subsetName,
                    offset: resultOffset,
                    profiles: results,
                });

                return {
                    total,
                    nextPage,
                    subsetName,
                    results: displayMode
                        ? results.map((profile) => convertProfileData({ profile, showRoll: true, showOrgUnit: true }))
                        : results,
                };
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
    },
};
